/**
 * The count room's paper, through the print root: the seed sheet (CR-02), the route sheet
 * (CR-03's paper backup), the Area Intake Form (SHEET mode — the 2026 form, unchanged), the
 * deposit slip (CR-07), the bag labels (CR-08, Code 128), the chain-of-custody PDF (CR-09).
 * Every figure is the API's; the page-count proof counts what these lay out.
 */
import type { ReactNode } from 'react';
import { FestivalClock } from '@cider/domain/engines';
import { Paper, PrintRoot, formatDateTime, formatMoney } from '@cider/ui';
import {
  LABEL,
  NOTES,
  type Bag,
  type DepositRow,
  type Pass,
  type Register,
} from '../views/money/CrShared.js';
import { useLoad } from '../views/money/shared.js';
import { code128Svg } from './code128.js';

/** The season the doors are scoped to: the fiscal year the day falls in, less one (Detroit's day). */
const seasonOf = (d: Date): number => {
  const w = FestivalClock.wallClock(d);
  return w.month >= 7 ? w.year : w.year - 1;
};

const Head = ({ title, facts }: { title: string; facts: string }): ReactNode => (
  <div className="report__head">
    <div className="report__eyebrow">SAINT LAWRENCE APPLE FEST · MONEY · COUNT ROOM</div>
    <h1>{title}</h1>
    <div className="report__facts">{facts}</div>
  </div>
);
const Bars = ({ code, height = 40 }: { code: string; height?: number }): ReactNode => (
  <span dangerouslySetInnerHTML={{ __html: code128Svg(code, { height, unit: 2 }) }} />
);

/** CR-02 · the seed sheet: one page, every register's seed and mix, a line for the bag scanned. */
export function SeedSheet({ year }: { year: string }): ReactNode {
  const regs = useLoad<{ registers: Register[] }>(`/money/countroom/${year}/registers`);
  const rows = regs.data?.registers ?? [];
  if (regs.error) return <p role="alert">{regs.error}</p>;
  const total = rows.reduce((n, r) => n + (r.profile?.seedCents ?? 0), 0);
  const mix = (r: Register): string =>
    r.profile
      ? [
          r.profile.mix.bills1 ? `${String(r.profile.mix.bills1)} × $1` : null,
          r.profile.mix.bills5 ? `${String(r.profile.mix.bills5)} × $5` : null,
          r.profile.mix.bills20 ? `${String(r.profile.mix.bills20)} × $20` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : 'no seed profile';
  return (
    <PrintRoot
      kind="letter"
      title={`Seed sheet ${year}`}
      pages={[
        <Paper
          key="1"
          masthead={`Seed sheet — ${year}`}
          submast={`${String(rows.length)} registers · ${formatMoney(total)} drawn`}
          stamp="SEED"
          rows={[
            ...rows.map((r) => ({
              key: r.id,
              a: `${r.name}${r.tag?.tagCode ? ` · ${r.tag.tagCode}` : ''}`,
              b: `${mix(r)} · bag ______ · chair __________`,
              c: r.profile ? formatMoney(r.profile.seedCents) : 'NO DATA',
              box: true,
            })),
            { key: 'total', a: 'Total drawn', c: formatMoney(total) },
          ]}
          foot="Each bag comes back with its drawer. It is counted as part of the area's close."
        />,
      ]}
    />
  );
}

/** CR-03 · the route sheet: the registers in walking order, a line per pickup. */
export function RouteSheet({ day }: { day: string }): ReactNode {
  const year = String(Number(day.slice(0, 4)) - (Number(day.slice(5, 7)) >= 7 ? 0 : 1));
  const route = useLoad<{
    registers: (Register & { lastPickupAt: string | null; pickups: number })[];
  }>(`/money/countroom/${year}/${day}/route`);
  if (route.error) return <p role="alert">{route.error}</p>;
  const rows = route.data?.registers ?? [];
  return (
    <PrintRoot
      kind="letter"
      title={`Route sheet ${day}`}
      pages={[
        <Paper
          key="1"
          masthead={`Pickup route — ${day}`}
          submast="walking order · the phone is the record; this is the paper backup"
          stamp={`${String(rows.length)} STOPS`}
          rows={[
            ...rows.map((r, i) => ({
              key: r.id,
              a: `${String(i + 1)}. ${r.name}${r.tag?.tagCode ? ` · ${r.tag.tagCode}` : ''}`,
              b: 'bag ________ · time ______ · chair OK ________',
              c: 'paid out $______ / ______',
              box: true,
            })),
            { key: 'escort', a: 'Escort', c: '____________________' },
            { key: 'runner', a: 'Runner', c: '____________________' },
          ]}
          foot="Two people on every pickup. The phone is the record; this sheet is typed in if the phone was down."
        />,
      ]}
    />
  );
}

/** SHEET mode · the Area Intake Form, the 2026 paper unchanged: one page per register. */
export function IntakeForms({ year }: { year: string }): ReactNode {
  const regs = useLoad<{ registers: Register[] }>(`/money/countroom/${year}/registers`);
  if (regs.error) return <p role="alert">{regs.error}</p>;
  const rows = regs.data?.registers ?? [];
  return (
    <PrintRoot
      kind="letter"
      title={`Area Intake Forms ${year}`}
      pages={rows.map((r) => (
        <div key={r.id} className="letter report">
          <Head
            title={`Area Intake Form · ${r.name}`}
            facts={`${year} · pickup ____ of the day · Friday / Saturday / Sunday`}
          />
          <table className="report__table">
            <tbody>
              {NOTES.map((k) => (
                <tr key={k}>
                  <td>{LABEL[k]}</td>
                  <td>count ________</td>
                  <td>$ ________</td>
                </tr>
              ))}
              <tr>
                <td>Coin</td>
                <td></td>
                <td>$ ________</td>
              </tr>
              <tr>
                <td>Checks</td>
                <td></td>
                <td>$ ________</td>
              </tr>
              <tr>
                <td>
                  <b>Total in the bag</b>
                </td>
                <td></td>
                <td>$ ________</td>
              </tr>
              <tr>
                <td>Seed (first pickup only)</td>
                <td>
                  from the profile: {r.profile ? formatMoney(r.profile.seedCents) : 'NO DATA'}
                </td>
                <td>$ ________</td>
              </tr>
            </tbody>
          </table>
          <p>Bank bag number: ____________ · Bag: ____________</p>
          <p>Prepared by: ____________________ · Second counter: ____________________</p>
          <p className="report__facts">
            Keyed at the count station as a SHEET count, photographed to the count. Two names, never
            one.
          </p>
        </div>
      ))}
    />
  );
}

/** CR-07 · the deposit slip: expected by denomination, the physical beside it, the serial, the names. */
export function DepositSlip({ id }: { id: string }): ReactNode {
  const custody = useLoad<{ events: { at: string; event: string; bagCode: string }[] }>(
    `/money/countroom/custody/deposit/${id}?year=${String(seasonOf(new Date()))}`,
  );
  const load = useLoad<DepositRow>(
    `/money/countroom/deposits/${id}?year=${String(seasonOf(new Date()))}`,
  );
  const dep = load.data && 'memo' in load.data ? load.data : null;
  if (load.error) return <p role="alert">{load.error}</p>;
  if (!dep) return <p>Reading…</p>;
  const keys = [...NOTES, 'coinCents', 'checkCents'] as const;
  const money = (k: (typeof keys)[number]): boolean => k === 'coinCents' || k === 'checkCents';
  return (
    <PrintRoot
      kind="letter"
      title={`Deposit slip${dep.bankBagSerial ? ` · bank bag ${dep.bankBagSerial}` : ''}${dep.festivalDay ? ` · ${dep.festivalDay}` : ''}`}
      pages={[
        <Paper
          key="1"
          masthead={`Deposit — ${dep.festivalDay ?? ''}`}
          submast={`Saint Lawrence Apple Fest · ${dep.status ?? 'open'}`}
          stamp={dep.bankBagSerial ? `BANK BAG ${dep.bankBagSerial}` : 'NO BANK BAG YET'}
          rows={[
            ...keys.map((k) => {
              const label = k === 'coinCents' ? 'Coin' : k === 'checkCents' ? 'Checks' : LABEL[k];
              const exp = money(k)
                ? formatMoney(dep.expected?.[k] ?? 0)
                : String(dep.expected?.[k] ?? 0);
              const phys = dep.physical
                ? money(k)
                  ? formatMoney(dep.physical[k])
                  : String(dep.physical[k])
                : 'NO DATA';
              return { key: k, a: label, b: `expected ${exp}`, c: phys };
            }),
            {
              key: 'total',
              a: 'Total',
              b: `expected ${formatMoney(dep.expected?.totalCents ?? 0)}`,
              c: dep.physical ? formatMoney(dep.physical.totalCents) : 'NO DATA',
            },
            {
              key: 'counts',
              a: 'Made from',
              b: `${String(dep.counts.length)} sealed counts · verified by ${dep.verifyMethod?.replace('_', ' ') ?? '—'}`,
            },
            {
              key: 'sealed',
              a: 'Sealed',
              b: `${dep.sealedAt ? formatDateTime(dep.sealedAt) : '—'} · released to ${dep.releasedTo ?? '—'}`,
            },
            ...(dep.oneHandSays ? [{ key: 'one', a: 'One hand', b: dep.oneHandSays }] : []),
          ]}
          big={dep.bankBagSerial ?? undefined}
          code={dep.bankBagSerial ? <Bars code={dep.bankBagSerial} /> : undefined}
          foot={`This slip and the books came from the same rows. ${String(custody.data?.events.length ?? 0)} custody events on this deposit.`}
        />,
      ]}
    />
  );
}

/** CR-08 · the bag labels: Code 128, eight to a letter page, AF-0001… and the seed bags. */
export function BagLabels({ year }: { year: string }): ReactNode {
  const bags = useLoad<{ bags: Bag[] }>(`/money/countroom/${year}/bags`);
  if (bags.error) return <p role="alert">{bags.error}</p>;
  const list = (bags.data?.bags ?? []).filter((b) => b.status !== 'retired');
  const perPage = 8;
  const pages: Bag[][] = [];
  for (let i = 0; i < list.length; i += perPage) pages.push(list.slice(i, i + perPage));
  return (
    <PrintRoot
      kind="letter"
      title={`Bag labels ${year}`}
      pages={(pages.length ? pages : [[]]).map((page, i) => (
        <div key={i} className="letter labels">
          {page.map((b) => (
            <div key={b.id} className="label">
              <div className="report__eyebrow">
                APPLE FEST · {b.bagType === 'seed' ? 'SEED BAG' : 'CASH BAG'} · {year}
              </div>
              <Bars code={b.bagCode} height={60} />
            </div>
          ))}
          {page.length === 0 ? (
            <p>No bags registered for {year}. Register the kit on Bag registry.</p>
          ) : null}
        </div>
      ))}
    />
  );
}

/** CR-09 · the chain-of-custody PDF: the lineage, forty events to a page. */
export function CustodyPages({ ref }: { ref: string }): ReactNode {
  const [kind, id] = ref.includes(':')
    ? [ref.slice(0, ref.indexOf(':')), ref.slice(ref.indexOf(':') + 1)]
    : ['bag', ref];
  const load = useLoad<{
    events: {
      id: string;
      at: string;
      event: string;
      bagCode: string;
      fromCustodian: string | null;
      toCustodian: string | null;
      note: string | null;
      device: string | null;
    }[];
    passes: Pass[];
  }>(
    `/money/countroom/custody/${kind}/${encodeURIComponent(id)}?year=${String(seasonOf(new Date()))}`,
  );
  if (load.error) return <p role="alert">{load.error}</p>;
  const events = load.data?.events ?? [];
  const pages: (typeof events)[] = [];
  for (let i = 0; i < events.length; i += 40) pages.push(events.slice(i, i + 40));
  return (
    <PrintRoot
      kind="letter"
      title={`Chain of custody ${id}`}
      pages={(pages.length ? pages : [[]]).map((page, i) => (
        <div key={i} className="letter report">
          <Head
            title={`Chain of custody · ${kind} ${id}`}
            facts={`${String(events.length)} events · immutable · page ${String(i + 1)} of ${String(Math.max(pages.length, 1))}`}
          />
          <table className="report__table">
            <thead>
              <tr>
                <th>When</th>
                <th>Bag</th>
                <th>Event</th>
                <th>From → to</th>
                <th>Device</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {page.map((e) => (
                <tr key={e.id}>
                  <td>{formatDateTime(e.at)}</td>
                  <td>{e.bagCode}</td>
                  <td>{e.event.replace(/_/g, ' ')}</td>
                  <td>
                    {e.fromCustodian ?? '—'} → {e.toCustodian ?? '—'}
                  </td>
                  <td>{e.device ?? ''}</td>
                  <td>{e.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {i === pages.length - 1 && (load.data?.passes.length ?? 0) > 0 ? (
            <p className="report__facts">
              Passes:{' '}
              {load.data?.passes
                .map(
                  (p) =>
                    `pass ${String(p.passNo)} ${formatMoney(p.totalCents)} (${p.source.replace(/_/g, ' ')})`,
                )
                .join(' · ')}
            </p>
          ) : null}
        </div>
      ))}
    />
  );
}
